/**
 * Action Intent Details Sidebar
 * Lists the sub-intents of an action intent. Each sub-intent is titled by its name
 * (with a stable fallback), shows its text as the body, and carries an Auto-send / Draft
 * toggle (per-index, via the existing endpoint), plus a read-only response policy.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  useApplyReadiness,
  useSetPromotedTemplateEnabled,
  useTemplateReadiness,
  useWorkflowBlueprintById
} from '@/hooks/use-promoted-blueprints';
import { usePermissions } from '@/hooks/use-permissions';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { getApiErrorMessage } from '@/lib/utils';
import { formatIntentLabel, getSubIntentDetail, getSubIntentName, isTemplateEnabled } from '../utils';
import type { WorkflowBlueprint } from '../types';
import { AutomationSection } from './automation-section';
import { TemplateGate } from './template-gate';

interface ActionIntentDetailsSidebarLabels {
  intentLabel?: string;
  subIntentsTitle?: string;
  noSubIntentsText?: string;
}

interface ActionIntentDetailsSidebarProps {
  blueprintId: string;
  onClose?: () => void;
  labels?: ActionIntentDetailsSidebarLabels;
}

export const ActionIntentDetailsSidebar = ({ blueprintId, onClose, labels }: ActionIntentDetailsSidebarProps) => {
  const { data: blueprint, isLoading } = useWorkflowBlueprintById(blueprintId);
  const setTemplateEnabledMutation = useSetPromotedTemplateEnabled();
  const { data: templateReadiness } = useTemplateReadiness(blueprintId);
  const applyReadinessMutation = useApplyReadiness();
  const { can } = usePermissions();
  const canEdit = can('edit', 'promoted-blueprint');
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  // Turning a sub-intent to auto-send puts a reply in front of customers; it asks first. Draft never does.
  const [confirmIndex, setConfirmIndex] = useState<number | null>(null);

  const intentLabel = labels?.intentLabel ?? 'Action Intent';
  const subIntentsTitle = labels?.subIntentsTitle ?? 'Sub-intents';
  const noSubIntentsText = labels?.noSubIntentsText ?? 'No sub-intents for this action intent.';

  const handleSubIntentToggle = async (index: number, enabled: boolean) => {
    if (!blueprint) return;
    const queryKey = ['promoted-blueprints', 'workflow', blueprint.id];
    queryClient.setQueryData<WorkflowBlueprint>(queryKey, (prev) =>
      prev ? { ...prev, templatesEnabled: { ...prev.templatesEnabled, [String(index)]: enabled } } : prev
    );
    setPendingIndex(index);
    try {
      await setTemplateEnabledMutation.mutateAsync({ id: blueprint.id, index, enabled });
      toast.success(enabled ? 'Sub-intent set to auto-send' : 'Sub-intent set to draft');
    } catch (error) {
      queryClient.setQueryData<WorkflowBlueprint>(queryKey, (prev) =>
        prev ? { ...prev, templatesEnabled: { ...prev.templatesEnabled, [String(index)]: !enabled } } : prev
      );
      // The gate's refusal says which type back-testing has not cleared; show that, not a generic failure.
      toast.error(getApiErrorMessage(error, 'Failed to update sub-intent'));
      console.error('Toggle sub-intent error:', error);
    } finally {
      setPendingIndex(null);
      invalidateQueries(queryKey);
    }
  };

  const hasLinkedTemplates = Boolean(templateReadiness?.some((template) => template.link));

  const handleApplyReadiness = async () => {
    try {
      const applied = await applyReadinessMutation.mutateAsync({ id: blueprintId });
      toast.success(
        applied.enabled.length || applied.disabled.length
          ? `Auto-send on for ${applied.enabled.length}, back to draft for ${applied.disabled.length}`
          : 'Every linked sub-intent already matches back-testing'
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not apply back-testing readiness'));
    }
  };

  return (
    <aside className='flex h-full w-[420px] shrink-0 flex-col border-l bg-background'>
      <div className='flex items-start justify-between gap-2 border-b px-5 py-4'>
        <div className='min-w-0'>
          <p className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>{intentLabel}</p>
          {isLoading || !blueprint ? (
            <Skeleton className='mt-1 h-5 w-40' />
          ) : (
            <>
              <h2 className='truncate text-sm font-semibold text-foreground'>
                {formatIntentLabel(blueprint.intent)}
              </h2>
              <p className='truncate font-mono text-[11px] text-muted-foreground'>{blueprint.intent}</p>
            </>
          )}
        </div>
        {onClose && (
          <Button variant='ghost' size='icon' className='size-7 shrink-0' onClick={onClose}>
            <X className='size-4' />
          </Button>
        )}
      </div>

      <div className='flex-1 overflow-y-auto px-5 py-4'>
        {isLoading || !blueprint ? (
          <div className='space-y-3'>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className='h-24 w-full rounded-lg' />
            ))}
          </div>
        ) : (
          <>
            <div className='mb-1 flex items-center justify-between'>
              <h3 className='text-xs font-semibold text-foreground'>{subIntentsTitle}</h3>
              <span className='text-[11px] text-muted-foreground'>{blueprint.templates.length} total</span>
            </div>
            <p className='mb-3 text-[11px] leading-relaxed text-muted-foreground'>
              <span className='font-medium text-foreground'>Auto-send</span> = replies for this sub-intent can be
              sent automatically.{' '}
              <span className='font-medium text-foreground'>Draft</span> = the sub-intent is still handled, but the
              reply always goes out as a draft for human review (never auto-sent).
            </p>

            {hasLinkedTemplates && canEdit && (
              <div className='mb-3 flex items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2'>
                <p className='text-[11px] leading-snug text-muted-foreground'>
                  Linked sub-intents auto-send only while back-testing clears their scenario type.
                </p>
                <Button size='sm' className='h-7 shrink-0 text-xs' onClick={handleApplyReadiness} disabled={applyReadinessMutation.isPending}>
                  Apply readiness
                </Button>
              </div>
            )}

            <div className='space-y-3'>
              {blueprint.templates.length === 0 && (
                <p className='text-xs text-muted-foreground'>{noSubIntentsText}</p>
              )}
              {blueprint.templates.map((template, index) => {
                const enabled = isTemplateEnabled(blueprint.templatesEnabled, index);
                const name = getSubIntentName(blueprint.templateNames, index);
                const detail = getSubIntentDetail(blueprint.templateDetails, index);
                const gate = templateReadiness?.find((row) => row.index === index);
                return (
                  <div
                    key={index}
                    className={`rounded-lg border transition-opacity ${enabled ? '' : 'opacity-55'}`}
                  >
                    <div className='flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2'>
                      <div className='flex min-w-0 items-center gap-2'>
                        <span className='truncate text-xs font-semibold text-foreground' title={name}>
                          {name}
                        </span>
                        <Badge variant={enabled ? 'default' : 'secondary'} className='text-[10px]'>
                          {enabled ? 'Auto-send' : 'Draft'}
                        </Badge>
                      </div>
                      <Switch
                        className='cursor-pointer'
                        checked={enabled}
                        aria-label={`Auto-send ${name}`}
                        onCheckedChange={(checked) =>
                          checked ? setConfirmIndex(index) : handleSubIntentToggle(index, false)
                        }
                        disabled={!canEdit || pendingIndex === index}
                      />
                    </div>
                    {gate && <TemplateGate blueprintId={blueprint.id} template={gate} canEdit={canEdit} />}
                    {detail && (
                      <div className='border-b bg-muted/20 px-3 py-2'>
                        <p className='mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'>
                          How this is reached
                        </p>
                        <p className='whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground'>
                          {detail.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                            part.startsWith('**') && part.endsWith('**') ? (
                              <strong key={i} className='font-semibold text-foreground'>
                                {part.slice(2, -2)}
                              </strong>
                            ) : (
                              part
                            )
                          )}
                        </p>
                      </div>
                    )}
                    <div className='max-h-64 overflow-y-auto whitespace-pre-wrap px-3 py-3 text-[13px] leading-relaxed text-foreground/90'>
                      {template}
                    </div>
                  </div>
                );
              })}
            </div>

            <AutomationSection blueprintId={blueprint.id} canEdit={canEdit} />

            <AlertDialog open={confirmIndex !== null} onOpenChange={(open) => !open && setConfirmIndex(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Set this sub-intent to auto-send?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {confirmIndex !== null && (
                      <>
                        Replies for <strong>{getSubIntentName(blueprint.templateNames, confirmIndex)}</strong> under{' '}
                        <code>{blueprint.intent}</code> will be sent to customers without a person reviewing them,
                        from the next matching ticket on.
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      if (confirmIndex !== null) void handleSubIntentToggle(confirmIndex, true);
                      setConfirmIndex(null);
                    }}
                  >
                    Set to auto-send
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {blueprint.responsePolicy !== undefined && blueprint.responsePolicy !== null && (
              <Accordion type='single' collapsible className='mt-4'>
                <AccordionItem value='policy' className='border-b-0'>
                  <AccordionTrigger className='text-xs font-semibold'>Response Policy</AccordionTrigger>
                  <AccordionContent>
                    <pre className='whitespace-pre-wrap rounded-md bg-muted p-3 text-[12px] leading-relaxed text-muted-foreground'>
                      {typeof blueprint.responsePolicy === 'string'
                        ? blueprint.responsePolicy
                        : JSON.stringify(blueprint.responsePolicy, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </>
        )}
      </div>
    </aside>
  );
};
